﻿// src/layouts/ProtectedShell.jsx
import { Outlet, useLocation } from "react-router-dom";
import { useRef } from "react";
import { useAuth } from "@/context/auth.js";
import { useTranslation } from "react-i18next";
import AppHeader from "../components/AppHeader.jsx";
import TopTabs from "../components/TopTabs.jsx";

/**
 * ProtectedShell — Tabs
 * - Tab "Geocerca" abre el MAPA (ruta /geocerca)
 * - La pantalla hub/listado queda en /geocercas (sin tab)
 * - Billing / Pricing visibles solo para perfiles de gestión
 * - Tracker debe quedar junto a "Invitar tracker"
 * - Recursos debe quedar al extremo derecho; Pricing y Billing quedan antes de Recursos
 */

function resolveResourcesPath(value) {
  const lang = String(value || "es").toLowerCase().trim().slice(0, 2);
  if (lang === "fr") return "/ressources";
  if (lang === "es") return "/recursos";
  return "/resources";
}

const incidentsAvailable = typeof window !== "undefined" && ["preview.tugeocercas.com", "app.tugeocercas.com"].includes(window.location.hostname);

function buildTabs({ role, isAppRoot, isAdmin, resourcesPath }) {
  const r = String(role || "").toLowerCase();

  const isTrackerOnly = r === "tracker";

  if (isTrackerOnly) {
    return [{ path: "/tracker-gps", labelKey: "app.tabs.tracker" }, ...(incidentsAvailable ? [{path:"/incidencias",labelKey:"app.tabs.incidents"}] : [])];
  }

  const tabs = [
    { path: "/inicio", labelKey: "app.tabs.inicio" },

    // MAPA / CONSTRUCTOR
    { path: "/geocerca", labelKey: "app.tabs.geocerca" },

    { path: "/personal", labelKey: "app.tabs.personal" },
    { path: "/actividades", labelKey: "app.tabs.actividades" },
    { path: "/asignaciones", labelKey: "app.tabs.asignaciones" },
    { path: "/visitas", labelKey: "app.tabs.visitas" },
    ...(incidentsAvailable ? [{path:"/incidencias",labelKey:"app.tabs.incidents"}] : []),
    { path: "/planificacion", labelKey: "app.tabs.planificacion" },
    { path: "/benchmarking", labelKey: "app.tabs.benchmarking" },
    { path: "/reportes", labelKey: "app.tabs.reportes" },
    { path: "/dashboard-costs", labelKey: "app.tabs.panelCostos" },
  ];

  if (isAdmin) {
    tabs.push(
      { path: "/dashboard", labelKey: "app.tabs.tracker" },
      { path: "/invitar-tracker", labelKey: "app.tabs.invitarTracker" },
      { path: "/pricing", labelKey: "app.tabs.pricing" },
      { path: "/billing", labelKey: "app.tabs.billing" }
    );
  } else {
    tabs.push({ path: "/dashboard", labelKey: "app.tabs.tracker" });
  }

  if (isAppRoot) {
    tabs.push({ path: "/admins", labelKey: "app.tabs.admins" });
  }

  tabs.push({ path: resourcesPath, labelKey: "app.tabs.resources" });

  return tabs;
}

export default function ProtectedShell() {
  const { i18n } = useTranslation();
  const {
    loading,
    user,
    currentRole,
    role,
    currentOrgId,
    currentOrg,
    orgId,
    isAppRoot,
    isAdmin,
  } = useAuth();
  const location = useLocation();
  const bypassLoggedRef = useRef(false);

  const isTrackerRoute =
    location.pathname.startsWith("/tracker") || location.pathname.startsWith("/tracker-gps");

  if (isTrackerRoute && user) {
    if (!bypassLoggedRef.current) {
      console.warn("[tracker-auth-bootstrap] source=ProtectedShell");
      console.warn("[tracker-auth-bootstrap] bypassed");
      console.warn("[ROOT-BYPASS] tracker flow unblocked at root");
      bypassLoggedRef.current = true;
    }
  }

  if (loading) return null;
  if (!user) return null;

  // Determinar rol y organización efectivos
  const effectiveRole = String(currentRole || role || "").toLowerCase().trim();
  const effectiveOrgId = String(currentOrgId || currentOrg?.id || orgId || "").trim();
  const resourcesPath = resolveResourcesPath(i18n?.resolvedLanguage || i18n?.language);

  const hasMissingContext = user && (!effectiveRole || !effectiveOrgId);

  const tabs = hasMissingContext
    ? [{ path: "/inicio", labelKey: "app.tabs.inicio" }]
    : buildTabs({ role: effectiveRole, isAppRoot, isAdmin, resourcesPath });

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <TopTabs tabs={tabs} />

      <main className="max-w-7xl mx-auto px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
